import { GameMap } from "../map";
import { GameClient, runDisplay, podOriginName } from "../net";
import { regionEliminate } from "../elimination";
import { STATIONS } from "../stations";
import { POI_CATEGORIES } from "../pois";
import { mountQuestions } from "./questions";
import { mountAsk } from "./ask";
import { mountClues } from "./clues";
import { downloadRoundRecap } from "../recap";
import { openHdVideo } from "./hdVideo";
import { mountInfo } from "./info";
import { mountSheet } from "./sheet";
import { mountMeasureTab } from "./measureTab";
import { mountGameMenu } from "./gameMenu";
import { uploadPhoto, compressImage } from "../api";
import { curseById, curseImage, resolveText, timerMinutes } from "../curses";
import { openDice } from "./dice";
import { esc } from "./util";

// Mobile-native MTA-themed Seeker. Full-bleed MapLibre map (the hero) with a
// floating signage bar + a gesture bottom sheet (Layers · Ask · Measure · Plan).
// Reuses the proven engines (questions, assist, messenger, run state); the new
// shell + Layers tile grid are bespoke. See design-handoff/ for the spec.

const TOTAL = STATIONS.length;

export interface Tile { id: string; label: string; emoji: string; color: string; layers?: string[]; hideElim?: boolean; }
const PLACE_TILES: Tile[] = POI_CATEGORIES.filter((c) => !c.polygon)
  .map((c) => ({ id: c.id, label: c.label, emoji: c.emoji, color: c.color, layers: [`poi-${c.id}-dot`, `poi-${c.id}-label`] }));
export const LAYER_GROUPS: { name: string; tiles: Tile[] }[] = [
  { name: "Board", tiles: [
    { id: "mask", label: "Grey mask", emoji: "▦", color: "#5C5038", layers: ["mask-fill"] },
    { id: "zones", label: "Zones", emoji: "◉", color: "#00933C", layers: ["zone-fill", "zone-outline"] },
    { id: "dots", label: "Station dots", emoji: "•", color: "#0B6B39", layers: ["dot"] },
    { id: "offlimits", label: "Off-limits", emoji: "⛔", color: "#FF6319", layers: ["offlimits-fill", "offlimits-line"] },
    { id: "hideelim", label: "Hide elim.", emoji: "🚫", color: "#231C12", hideElim: true },
  ] },
  { name: "Places", tiles: PLACE_TILES },
  { name: "Areas & overlays", tiles: [
    { id: "parks", label: "Parks", emoji: "🌳", color: "#229954", layers: ["park-fill", "park-line", "park-label"] },
    { id: "airports", label: "Airports", emoji: "✈️", color: "#2563EB", layers: ["airport-dot", "airport-label"] },
    { id: "congressional", label: "Congressional", emoji: "🏛️", color: "#9333EA", layers: ["div-congressional-fill", "div-congressional-line", "div-congressional-label"] },
    { id: "communityboard", label: "Comm. boards", emoji: "🗳️", color: "#1E8449", layers: ["div-communityboard-fill", "div-communityboard-line", "div-communityboard-label"] },
    { id: "neighborhood", label: "Neighborhoods", emoji: "🏘️", color: "#C99A2E", layers: ["div-neighborhood-fill", "div-neighborhood-line", "div-neighborhood-label"] },
  ] },
];
// Tiles that start ON (match the map's default layer visibility).
export const DEFAULT_ON = new Set(["mask", "zones", "dots", "offlimits", "airports"]);

// Admin-division overlays whose districts get tappable select pins.
export const DIVISION_IDS = new Set(["congressional", "communityboard", "neighborhood"]);
// Tapping a district pin offers Eliminate-inside / Keep-only (isolate). Shared
// by the Seeker board and Calculator mode.
export function makeDivisionPicker(gameMap: GameMap, client: GameClient) {
  return (name: string, geometry: GeoJSON.Geometry, at: [number, number]) => {
    const doRegion = (mode: "eliminate-inside" | "keep-inside", label: string, msg: (n: number) => string) => {
      const ids = regionEliminate(geometry, mode).filter((id) => client.statusOf(id) === "active");
      if (!ids.length) { alert("No active stations to change for that district."); return; }
      if (!confirm(msg(ids.length))) return;
      client.eliminate(ids, label);
    };
    gameMap.showActionPopup(at, name, [
      { label: "⛔ Eliminate inside", danger: true, fn: () => doRegion("eliminate-inside", `Eliminated inside ${name}`, (n) => `Eliminate ${n} station${n === 1 ? "" : "s"} inside ${name}?`) },
      { label: "◎ Keep only this (isolate)", fn: () => doRegion("keep-inside", `Isolated to ${name}`, (n) => `Keep only ${name} — eliminate the ${n} station${n === 1 ? "" : "s"} outside it?`) },
    ]);
  };
}

type Tab = "layers" | "tools" | "clues" | "plan";
type Sub = "ask" | "measure" | "manual";
const TABS: { id: Tab; icon: string; label: string }[] = [
  { id: "layers", icon: "▦", label: "Layers" },
  { id: "tools", icon: "🛠", label: "Tools" },
  { id: "clues", icon: "🔍", label: "Clues" },
  { id: "plan", icon: "👥", label: "Team" },
];

export function mountSeeker(root: HTMLElement, client: GameClient, opts: { onExit?: () => void; title: string }) {
  document.body.classList.add("sk-active");
  root.innerHTML = `
    <div class="sk">
      <div id="map" class="sk-map"></div>

      <div class="sk-top">
        <button class="nav-btn" id="game-menu-btn" aria-label="Open menu"><span></span><span></span><span></span></button>
        <div class="sk-top-r">
          <div class="sk-fabs">
            <button class="sk-fab sk-fab-dice" id="sk-dice" aria-label="Roll dice">🎲</button>
            <button class="sk-fab sk-fab-loc" id="sk-locate" aria-label="Locate me">◎</button>
          </div>
          <div class="sk-cursechip" id="sk-cursechip" hidden>💀 CURSED</div>
          <div class="sk-runchip" id="sk-runchip" hidden><span class="sk-runchip-dot"></span><span id="sk-runchip-clk">--:--</span></div>
        </div>
      </div>
      <div class="sk-toolbadge" id="sk-toolbadge" hidden></div>

      <div class="sk-sheet" id="sk-sheet">
        <div class="sk-grab" id="sk-grab"><span></span><button class="sk-sheet-toggle" id="sk-sheet-toggle" aria-label="Hide panel" title="Hide panel">▾</button></div>
        <div class="sk-brand">
          <span class="sk-dots"><i style="background:#EE352E"></i><i style="background:#FCCC0A"></i><i style="background:#00933C"></i></span>
          <span class="sk-wordmark">JET LAG NYC</span>
          <span class="sk-dots"><i style="background:#0039A6"></i><i style="background:#FF6319"></i><i style="background:#B933AD"></i></span>
        </div>
        <div class="sk-tabs" id="sk-tabs">
          ${TABS.map((t) => { const pod = t.id === "plan" && client.mode === "podrotation"; return `<button class="sk-tab" data-tab="${t.id}"><span class="sk-tab-ic">${pod ? "🎯" : t.icon}</span>${pod ? "Squad" : t.label}</button>`; }).join("")}
        </div>
        <div class="sk-ro-banner" id="sk-ro-banner" hidden>👁 Viewing the <b>team leader's map</b> — read-only. Switch to <b>My map</b> in the Team tab to edit.</div>
        <div class="sk-body jl-scroll" id="sk-body">
          <section data-panel="layers" id="sk-layers"></section>
          <section data-panel="tools" id="sk-tools" hidden>
            <div class="sk-mode-seg" id="sk-subseg">
              <button data-sub="ask" class="on">❓ Ask</button>
              <button data-sub="measure">📐 Measure</button>
              <button data-sub="manual">✂️ Manual</button>
            </div>
            <section id="sk-ask-q"></section>
            <section id="sk-ask" hidden></section>
            <div class="sk-mode-seg" id="sk-mmodes" hidden>
              <button data-mmode="measure" class="on">📐 Measure</button>
              <button data-mmode="close">📍 How close am I?</button>
            </div>
            <div id="sk-measure-basic" hidden></div>
            <div id="sk-assist" hidden></div>
            <p class="sk-note" id="sk-measure-note" hidden></p>
          </section>
          <section data-panel="clues" id="sk-clues" hidden></section>
          <section data-panel="plan" id="sk-plan" hidden></section>
        </div>
      </div>
    </div>`;

  const gameMap = new GameMap(root.querySelector<HTMLElement>("#map")!);
  gameMap.onStationClick = (id) => { gameMap.select(id); gameMap.flyToStation(id); gameMap.showStationPopup(id); };
  // The map is full-bleed (inset:0); make sure its canvas tracks the real
  // viewport so it fills the screen (esp. after the sheet collapses on mobile).
  const fitMap = () => gameMap.map.resize();
  window.addEventListener("resize", fitMap);
  window.visualViewport?.addEventListener("resize", fitMap);
  requestAnimationFrame(fitMap);
  setTimeout(fitMap, 350);

  // ---- bottom sheet (created before the tools so they can collapse it) ----
  const sheet = mountSheet(root, () => gameMap.map.resize());

  // Active-tool badge (under the top bar): live tool name + quick undo, kept
  // visible when a tool collapses the sheet to a full-screen map.
  const toolBadge = root.querySelector<HTMLElement>("#sk-toolbadge")!;
  let tbLabel: string | null = null, tbN = 0, tbCommit: (() => void) | null = null;
  function renderBadge() {
    toolBadge.hidden = !tbLabel;
    if (!tbLabel) { toolBadge.innerHTML = ""; return; }
    toolBadge.innerHTML = `<span class="tb-name">🛠 ${tbLabel}</span>${tbN > 0 && tbCommit ? `<button class="tb-elim">Eliminate ${tbN}</button>` : ""}<button class="tb-undo" ${client.board.canUndo ? "" : "disabled"}>↶ Undo</button>`;
    toolBadge.querySelector(".tb-elim")?.addEventListener("click", () => tbCommit?.());
    toolBadge.querySelector(".tb-undo")?.addEventListener("click", () => client.undoLast());
  }

  // Ask = the four real Jet Lag question types (Matching/Measuring/Thermo/Radar);
  // Manual = the by-hand Cut/Draw tools (kept, but they aren't "questions").
  const ask = mountAsk(root.querySelector<HTMLElement>("#sk-ask-q")!, client, gameMap, { setFullscreen: (on) => sheet.collapse(on) });
  const clues = mountClues(root.querySelector<HTMLElement>("#sk-clues")!, client);
  const questions = mountQuestions(root.querySelector<HTMLElement>("#sk-ask")!, client, gameMap, {
    exclude: ["measure", "radar", "thermometer"], setFullscreen: (on) => sheet.collapse(on),
    onActive: (l) => { tbLabel = l; if (!l) { tbN = 0; tbCommit = null; } renderBadge(); },
    onPreview: (n, c) => { tbN = n; tbCommit = c; renderBadge(); },
  });
  mountInfo(client); // floating fab hidden by CSS; triggered from the menu
  // Locate: drop a persistent draggable "you are here" GPS pin and fly to it.
  // Tapping again re-fetches GPS and snaps the pin back (revert-to-GPS); the pin
  // can be dragged to test a hypothetical spot in the meantime. This is purely
  // the seeker's own location — it never references the hider.
  root.querySelector("#sk-locate")!.addEventListener("click", () => {
    navigator.geolocation?.getCurrentPosition(
      (p) => {
        const ll: [number, number] = [p.coords.longitude, p.coords.latitude];
        gameMap.snapMyLocation(ll);
        gameMap.map.flyTo({ center: ll, zoom: Math.max(gameMap.map.getZoom(), 15) });
      },
      () => alert("Couldn't get your location."),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 8000 });
  });
  root.querySelector("#sk-dice")?.addEventListener("click", () => openDice());
  root.querySelector("#sk-cursechip")?.addEventListener("click", () => openCurseModal());

  // ---- Measure tab: general measure (front & center) + how-close assist --
  const measure = mountMeasureTab(root, client, gameMap, "Drag the A &amp; B handles — straight-line distance, nothing eliminated.", (on) => sheet.collapse(on));

  // ---- Game menu (☰): identity, stations-left, schedule, info, exit ------
  mountGameMenu(root, client, { onExit: opts.onExit, stat: () => String(client.activeCount()) });

  // ---- tabs — the "Tools" tab has Ask / Measure / Manual sub-modes --------
  let tab: Tab = "layers";
  let sub: Sub = "ask";
  function setSub(s: Sub) {
    const prev = sub;
    sub = s;
    root.querySelectorAll<HTMLButtonElement>("#sk-subseg [data-sub]").forEach((b) => b.classList.toggle("on", b.dataset.sub === s));
    if (prev === "ask" && s !== "ask") ask.leave();
    if (prev === "manual" && s !== "manual") questions.deactivate();
    if (prev === "measure" && s !== "measure") measure.leave();
    root.querySelector<HTMLElement>("#sk-ask-q")!.hidden = s !== "ask";
    root.querySelector<HTMLElement>("#sk-ask")!.hidden = s !== "manual";
    root.querySelector<HTMLElement>("#sk-measure-basic")!.hidden = s !== "measure";
    // The 📐 Measure / 📍 How close am I? toggle + assist panel belong to the
    // measure sub-mode only; hide them (and the assist) when leaving it.
    root.querySelector<HTMLElement>("#sk-mmodes")!.hidden = s !== "measure";
    if (s !== "measure") { root.querySelector<HTMLElement>("#sk-assist")!.hidden = true; root.querySelector<HTMLElement>("#sk-measure-note")!.hidden = true; }
    if (s === "ask") ask.enter();
    else if (s === "measure") measure.enter("measure");
    // manual: questions panel is shown; nothing else to do (it self-renders)
  }
  function setTab(t: Tab) {
    const prev = tab;
    tab = t;
    root.querySelectorAll<HTMLElement>("[data-panel]").forEach((p) => { p.hidden = p.dataset.panel !== t; });
    root.querySelectorAll<HTMLButtonElement>(".sk-tab").forEach((b) => b.classList.toggle("on", b.dataset.tab === t));
    if (prev === "tools" && t !== "tools") { ask.leave(); questions.deactivate(); measure.leave(); }
    if (prev === "clues" && t !== "clues") clues.leave();
    if (sheet.name === "peek") sheet.set("half");
    if (t === "plan") renderPlan();
    if (t === "tools") setSub(sub);
    if (t === "clues") clues.enter();
  }
  root.querySelectorAll<HTMLButtonElement>(".sk-tab").forEach((b) => b.addEventListener("click", () => setTab(b.dataset.tab as Tab)));
  root.querySelectorAll<HTMLButtonElement>("#sk-subseg [data-sub]").forEach((b) => b.addEventListener("click", () => setSub(b.dataset.sub as Sub)));

  // ---- Layers tab (bespoke tile grid) -----------------------------------
  const layerOn = new Map<string, boolean>();
  const pickDivision = makeDivisionPicker(gameMap, client);
  function applyTile(t: Tile, on: boolean) {
    layerOn.set(t.id, on);
    if (t.hideElim) gameMap.setHideEliminated(on, client);
    else t.layers?.forEach((id) => gameMap.setLayerVisible(id, on));
    if (DIVISION_IDS.has(t.id)) gameMap.setDivisionPins(t.id, on, pickDivision);
  }
  function renderLayers() {
    const total = [...layerOn.values()].filter(Boolean).length;
    const el = root.querySelector<HTMLElement>("#sk-layers")!;
    el.innerHTML = `
      <div class="sk-layers-head"><span class="sk-seclabel">MAP LAYERS</span><span class="sk-on-count">${total} on</span></div>
      ${LAYER_GROUPS.map((g) => `
        <div class="sk-group">
          <div class="sk-group-name">${g.name}</div>
          <div class="sk-tiles">
            ${g.tiles.map((t) => { const on = !!layerOn.get(t.id); return `
              <button class="sk-tile ${on ? "on" : ""}" data-tile="${t.id}" style="--c:${t.color}">
                <span class="sk-tile-dot">${t.emoji}</span><span class="sk-tile-lbl">${t.label}</span>
              </button>`; }).join("")}
          </div>
        </div>`).join("")}
      <p class="sk-note">Tap to flip a layer instantly — the tile fills with its map color, so the legend <i>is</i> the control.</p>`;
    el.querySelectorAll<HTMLButtonElement>("[data-tile]").forEach((b) => b.addEventListener("click", () => {
      const t = LAYER_GROUPS.flatMap((g) => g.tiles).find((x) => x.id === b.dataset.tile)!;
      applyTile(t, !layerOn.get(t.id));
      renderLayers();
    }));
  }

  // ---- Plan tab (run · remaining · pins/notes · Q&A · history) ----------
  // Seekers care about the HIDER's run (head-start countdown → elapsed), not
  // their own team's run (which never starts, so client.run is always idle).
  function hiderRunDisplay() {
    // Pod rotation keys the hiding run by the round's pod, not a team.
    const key = client.mode === "podrotation" ? `pod${client.pod?.roundIdx ?? 0}` : client.mode === "group" ? "__hiders__" : (client.hiderTeamId || "");
    return runDisplay(client.runsAll[key] ?? null, client.config.headStartSec);
  }
  // Pod Rotation: assignment card for the seeker's current squad + "found" button.
  function podBanner(): string {
    const p = client.pod; if (!p?.schedule) return "";
    const round = p.roundIdx;
    if (round >= p.schedule.pods.length) {
      return `<div class="sk-card pod-banner"><div class="sk-seclabel">GAME COMPLETE</div>
        <div class="sk-phasesub">All ${p.schedule.pods.length} rounds are done. Open the <b>☰ menu → Game info</b> for the final standings.</div>
        <button class="primary" id="recap-dl" style="margin-top:8px">🎬 Download recap GIF</button>
        <button class="primary" id="hd-video" style="margin-top:6px">🎞 HD video (themed)</button></div>`;
    }
    // The squad roster must reflect the GM's live swing-move overrides, not just
    // the static locked schedule — otherwise rebalancing squads mid-round never
    // shows up on players' screens. Effective squad key = override else base squad
    // (same logic the GM console uses), then list every seeker sharing my key.
    const roundSquads = p.schedule.rounds[round] ?? [];
    const effKey = (id: string) => p.swingSquad?.[id] || (() => { const idx = roundSquads.findIndex((sq) => sq.includes(id)); return idx >= 0 ? `r${round}q${idx}` : ""; })();
    const seekersThisRound = p.schedule.pods.flat().filter((id) => !(p.schedule!.pods[round] ?? []).includes(id));
    const squad = p.mySquad ? seekersThisRound.filter((id) => effKey(id) === p.mySquad) : [];
    // The hide ORIGIN: round 1 = the GM's start point; later rounds = the station
    // the previous pod was caught at. It's a STARTING reference, not a live spot.
    const originName = podOriginName(p.origin);
    const startLine = round === 0
      ? `The pod <b>started from the game start point:</b> ${esc(originName)}`
      : `The pod <b>started from where the last pod was caught:</b> ${esc(originName)}`;
    const nm = (id: string) => esc(p.names?.[id] || id.slice(0, 5));
    // Seeking only begins once the pod's head start is over. While it's counting
    // down, hide the "found" button so a squad can't report a catch (which would
    // bank 0:00 of hide time) before they're even allowed to seek.
    const inHeadStart = hiderRunDisplay().phase === "headstart";
    const foundOrWait = p.pending
      ? `<div class="sk-phasesub" style="color:var(--green)">✓ A squad reported the catch — awaiting the GM.</div>
        <button class="primary" id="recap-dl" style="margin-top:8px">🎬 Download recap GIF</button>
        <button class="primary" id="hd-video" style="margin-top:6px">🎞 HD video (themed)</button>`
      : inHeadStart
        ? `<div class="sk-phasesub" style="margin-top:8px;color:var(--orange,#FF6319);font-weight:700">🕒 Head start in progress — seeking begins when it hits 0:00.</div>`
        : `<button class="primary green" id="pod-found" style="margin-top:8px">🎯 We found the pod!</button>`;
    return `<div class="sk-card pod-banner">
      <div class="sk-seclabel">ROUND ${round + 1} · YOU'RE SEEKING</div>
      <div style="font-size:13px;font-weight:700">Squad: ${squad.map(nm).join(", ") || "—"}</div>
      <div class="sk-phasesub">🚇 ${startLine}</div>
      <div class="sk-phasesub" style="opacity:.7;font-size:11px;margin-top:2px">That's only their starting point — there's no live tracking of the pod.</div>
      ${foundOrWait}
    </div>`;
  }
  // Team-leader canonical map: view toggle + claim/release/handoff/push/approve/revert.
  function nameOf(sub: string): string {
    if (sub === client.identity.sub) return "you";
    return client.team?.members.find((m) => m.sub === sub)?.name || client.pod?.names?.[sub] || "a teammate";
  }
  function teamMapCard(): string {
    const t = client.team;
    if (!t) return "";
    const view = client.activeView;
    const seg = `
      <div class="sk-mode-seg sk-mapview">
        <button data-view="mine" class="${view === "mine" ? "on" : ""}">My map</button>
        <button data-view="canonical" class="${view === "canonical" ? "on" : ""}">Team leader's map</button>
      </div>`;
    let lead = "";
    if (!t.leaderId) {
      lead = `<div class="sk-phasesub">No team leader yet. Claim it to make <b>your map</b> the team's shared canonical map.</div>
        <button class="primary" id="tm-claim" style="margin-top:6px">Claim team leader</button>`;
    } else if (client.isLeader) {
      const pushes = t.pushes.map((p) => `
        <div class="sk-pin-row" style="justify-content:space-between">
          <span>📥 <b>${esc(p.fromName)}</b> pushed their map</span>
          <button class="primary green" data-approve="${esc(p.from)}" style="padding:4px 10px">Approve</button>
        </div>`).join("");
      const others = t.members.filter((m) => m.sub !== client.identity.sub);
      const handoff = others.length ? `
        <div class="sk-pin-row" style="gap:6px;margin-top:6px">
          <select id="tm-handoff-sel" style="flex:1">${others.map((m) => `<option value="${esc(m.sub)}">${esc(m.name)}</option>`).join("")}</select>
          <button class="ghost" id="tm-handoff">Hand off</button>
        </div>` : "";
      lead = `<div class="sk-phasesub">You are the <b>team leader</b> — edits to this map are the team's canonical map.</div>
        ${pushes ? `<div class="sk-seclabel" style="margin-top:8px">PENDING PUSHES</div>${pushes}` : `<div class="sk-empty">No pending pushes from teammates.</div>`}
        <button class="ghost" id="tm-merge" style="width:100%;margin-top:6px">⤴ Merge my map into the team map</button>
        <div class="sk-pin-row" style="gap:6px;margin-top:6px">
          <button class="ghost" id="tm-revert" ${t.canRevert ? "" : "disabled"}>↶ Revert last approve</button>
          <button class="ghost" id="tm-release">Release leadership</button>
        </div>
        ${handoff}`;
    } else {
      lead = `<div class="sk-phasesub">Team leader: <b>${esc(nameOf(t.leaderId))}</b>. Their map is the team's canonical map.</div>
        <button class="primary" id="tm-push" style="margin-top:6px">📤 Push my map to the leader</button>`;
    }
    return `<div class="sk-card sk-teammap">
      <div class="sk-seclabel">TEAM MAP</div>
      ${seg}
      ${view === "canonical" && !client.isLeader ? `<div class="sk-phasesub" style="color:var(--orange,#FF6319)">👁 Read-only — you're viewing the leader's map.</div>` : ""}
      ${lead}
    </div>`;
  }
  function wireTeamMap(el: HTMLElement) {
    el.querySelectorAll<HTMLButtonElement>("[data-view]").forEach((b) => b.addEventListener("click", () => client.setView(b.dataset.view as "mine" | "canonical")));
    el.querySelector("#tm-claim")?.addEventListener("click", () => { client.claimLeader(); client.setView("canonical"); });
    el.querySelector("#tm-release")?.addEventListener("click", () => { client.releaseLeader(); client.setView("mine"); });
    el.querySelector("#tm-push")?.addEventListener("click", () => {
      client.pushToLeader();
      const b = el.querySelector<HTMLButtonElement>("#tm-push"); if (b) { b.textContent = "✓ Pushed — awaiting the leader"; b.disabled = true; }
    });
    el.querySelector("#tm-revert")?.addEventListener("click", () => client.revertCanonical());
    el.querySelector("#tm-merge")?.addEventListener("click", () => { if (confirm("Merge your personal map's eliminations into the team map? This adds everything you've ruled out to the shared canonical map (you can Revert it). Teammates will see the combined map.")) client.mergePersonalToCanonical(); });
    el.querySelectorAll<HTMLButtonElement>("[data-approve]").forEach((b) => b.addEventListener("click", () => client.approvePush(b.dataset.approve!)));
    el.querySelector("#tm-handoff")?.addEventListener("click", () => {
      const sel = el.querySelector<HTMLSelectElement>("#tm-handoff-sel");
      if (sel?.value && confirm(`Hand off team leadership to ${sel.options[sel.selectedIndex].text}?`)) { client.handoffLeader(sel.value); client.setView("mine"); }
    });
  }
  // The active-curse card: shows every un-cleared curse on this squad with the
  // right clear action (send a photo / mark done / wait for hiders / countdown).
  function curseCard(): string {
    const active = client.myCurses.filter((c) => c.status !== "cleared");
    if (!active.length) return "";
    const size = client.gameSize;
    return `<div class="sk-card sk-cursecard">
      <div class="sk-seclabel">💀 ${active.length === 1 ? "YOUR SQUAD IS CURSED" : `${active.length} CURSES ON YOUR SQUAD`}</div>
      ${active.map((c) => {
        const def = curseById(c.curseId);
        const clear = def?.clear;
        let action: string;
        if (c.status === "pending-verify") action = `<div class="sk-curse-wait">📸 Photo sent — waiting for the hiders to verify.</div>`;
        else if (!clear || clear.mode === "none") action = `<div class="sk-curse-wait">No way to lift this — it lasts the round.</div>`;
        else if (clear.mode === "hider") action = `<div class="sk-curse-wait">Only the hiders can lift this one.</div>`;
        else if (clear.mode === "photo") action = `<button class="primary sk-curse-photo" data-inst="${esc(c.instanceId)}">📸 Send a photo to clear${clear.prompt ? ` — ${esc(clear.prompt)}` : ""}</button>`;
        else if (clear.mode === "notify") action = `<button class="primary green sk-curse-done" data-inst="${esc(c.instanceId)}">✓ ${esc(clear.prompt || "Mark done")}</button>`;
        else { // timer
          const remMs = c.ts + timerMinutes(clear, size) * 60000 - Date.now();
          action = remMs > 0
            ? `<div class="sk-curse-wait">⏳ Lifts in ${Math.ceil(remMs / 60000)} min…</div>`
            : `<button class="primary green sk-curse-done" data-inst="${esc(c.instanceId)}">✓ Time served — clear</button>`;
        }
        return `<div class="sk-curse-item">
          ${def ? `<img class="sk-curse-img" src="${curseImage(def.id)}" alt="${esc(def.name)}" />` : ""}
          <div class="sk-curse-body">
            <div class="sk-curse-name">${def?.emoji || "💀"} ${esc(def?.name || c.curseId)}</div>
            <div class="sk-phasesub">${esc(resolveText(def?.description || "A curse is in effect.", def, size))}</div>
            <div class="sk-curse-from">Cast by ${esc(c.fromName)}</div>
            ${def?.dice ? `<button class="ghost sk-curse-dice" data-dice="${def.dice}">🎲 Roll ${def.dice === 1 ? "a die" : `${def.dice} dice`}</button>` : ""}
            ${action}
          </div>
        </div>`;
      }).join("")}
    </div>`;
  }
  // Tapping the 💀 CURSED badge opens this modal — every active curse, its clear
  // action, dice, and a MANUAL override clear (for stuck curses / bug recovery).
  function openCurseModal() {
    document.querySelectorAll("#sk-curse-modal").forEach((x) => x.remove());
    const size = client.gameSize;
    const active = client.myCurses.filter((c) => c.status !== "cleared");
    const modal = document.createElement("div");
    modal.id = "sk-curse-modal"; modal.className = "h-zoom-backdrop";
    modal.innerHTML = `<div class="h-zoom-card" style="max-width:420px;text-align:left">
      <div class="sk-seclabel" style="display:flex;justify-content:space-between;align-items:center">💀 ACTIVE CURSES<button class="ghost" id="cm-x" style="padding:2px 8px">✕</button></div>
      ${active.length ? active.map((c) => {
        const def = curseById(c.curseId);
        return `<div class="sk-curse-item">
          ${def ? `<img class="sk-curse-img" src="${curseImage(def.id)}" alt="" />` : ""}
          <div class="sk-curse-body">
            <div class="sk-curse-name">${def?.emoji || "💀"} ${esc(def?.name || c.curseId)}</div>
            <div class="sk-phasesub">${esc(resolveText(def?.description || "A curse is in effect.", def, size))}</div>
            <div class="sk-curse-from">Cast by ${esc(c.fromName)} · ${esc(c.status)}</div>
            ${def?.dice ? `<button class="ghost sk-curse-dice" data-dice="${def.dice}">🎲 Roll ${def.dice === 1 ? "a die" : `${def.dice} dice`}</button>` : ""}
            <button class="ghost cm-clear" data-inst="${esc(c.instanceId)}">🧹 Clear manually</button>
          </div>
        </div>`;
      }).join("") : `<div class="sk-empty">No active curses on your squad.</div>`}
      <p class="sk-note">“Clear manually” is an override for stuck curses — prefer the proper clear action in the Plan tab when you can.</p>
    </div>`;
    document.body.appendChild(modal);
    const close = () => modal.remove();
    modal.addEventListener("click", (e) => { if (e.target === modal) close(); });
    modal.querySelector("#cm-x")?.addEventListener("click", close);
    modal.querySelectorAll<HTMLButtonElement>(".sk-curse-dice").forEach((b) => b.addEventListener("click", () => openDice(+b.dataset.dice! as 1 | 2)));
    modal.querySelectorAll<HTMLButtonElement>(".cm-clear").forEach((b) => b.addEventListener("click", () => { client.submitCurseClear(b.dataset.inst!, "notify"); close(); }));
  }
  // "We caught them!" on the seeker board — records the catch + banks the hider's
  // run WITHOUT forcing this team to hide next (the GM sets the next hider). Pods
  // use the dedicated "We found the pod!" flow instead.
  function caughtCard(): string {
    if (client.mode === "podrotation" || client.role !== "seeker") return "";
    const d = hiderRunDisplay();
    if (d.phase !== "running") return ""; // only while a hider run is live
    return `<div class="sk-card"><div class="sk-seclabel">CAUGHT THE HIDER?</div>
      <button class="primary green" id="sk-caught" style="width:100%">🎯 We caught the hider!</button>
      <p class="sk-note">Banks their run + scores the catch. The GM sets who hides next.</p></div>`;
  }
  function planHtml(): string {
    const d = hiderRunDisplay();
    const remaining = client.activeCount();
    const pct = Math.round((remaining / TOTAL) * 100);
    const pins = client.board.pins, notes = client.board.notes;
    const thread = client.threads[client.seekerKey] || [];
    const log = client.board.log;
    return `
      ${teamMapCard()}
      ${podBanner()}
      ${caughtCard()}
      ${curseCard()}
      <div class="sk-plan-cards">
        <div class="sk-card">
          <div class="sk-seclabel">${d.phase === "headstart" || d.phase === "idle" ? "HEAD START" : "HIDER RUN"}</div>
          <div class="sk-clock">${d.text}</div>
        </div>
        <div class="sk-card">
          <div class="sk-seclabel">REMAINING</div>
          <div class="sk-remain-big"><span>${remaining}</span><small>/ ${TOTAL}</small></div>
          <div class="sk-bar"><i style="width:${pct}%"></i></div>
        </div>
      </div>
      <div class="sk-seclabel">PINS &amp; NOTES</div>
      <div class="sk-pins">
        ${[...pins.map((p) => ({ c: "#2563EB", t: p.label || "Pin" })), ...notes.map((n) => ({ c: "#C99A2E", t: n.text }))]
          .map((x) => `<div class="sk-pin-row"><span class="sk-pin-dot" style="background:${x.c}"></span><span>${esc(x.t)}</span></div>`).join("")
          || `<div class="sk-empty">No pins or notes yet — long-press the map to drop one.</div>`}
      </div>
      <div class="sk-seclabel">Q&amp;A WITH HIDER</div>
      <div class="sk-thread">
        ${thread.length ? thread.map((m) => `<div class="sk-msg ${m.fromHider ? "them" : "me"}"><span class="sk-msg-cat">${m.fromName}${m.category ? " · " + esc(m.category) : ""}</span>${esc(m.text)}</div>`).join("")
          : `<div class="sk-empty">No questions asked yet.</div>`}
      </div>
      <div class="sk-msg-input">
        <input id="sk-msg" placeholder="Ask the hider…" />
        <button id="sk-send">Send</button>
      </div>
      <div class="sk-seclabel">HISTORY</div>
      <div class="sk-hist">
        ${log.length ? log.slice().reverse().map((h) => `<div class="sk-hist-row"><span class="sk-hist-t">${clockTime(h.ts)}</span><span>${esc(h.text)}${h.by ? ` <em class="sk-hist-by">· ${esc(h.by)}</em>` : ""}</span></div>`).join("")
          : `<div class="sk-empty">No eliminations yet.</div>`}
      </div>`;
  }
  function renderPlan() {
    const el = root.querySelector<HTMLElement>("#sk-plan")!;
    if (el.contains(document.activeElement) && (document.activeElement as HTMLElement)?.id === "sk-msg") return; // don't clobber typing
    el.innerHTML = planHtml();
    const input = el.querySelector<HTMLInputElement>("#sk-msg")!;
    const send = () => { const v = input.value.trim(); if (v) { client.postMessage(client.seekerKey, v); input.value = ""; } };
    el.querySelector("#sk-send")!.addEventListener("click", send);
    input.addEventListener("keydown", (e) => { if (e.key === "Enter") send(); });
    el.querySelectorAll<HTMLButtonElement>(".sk-curse-dice").forEach((b) => b.addEventListener("click", () => openDice(+b.dataset.dice! as 1 | 2)));
    el.querySelectorAll<HTMLButtonElement>(".sk-curse-done").forEach((b) => b.addEventListener("click", () => client.submitCurseClear(b.dataset.inst!, "notify")));
    el.querySelectorAll<HTMLButtonElement>(".sk-curse-photo").forEach((b) => b.addEventListener("click", () => {
      const input = document.createElement("input");
      input.type = "file"; input.accept = "image/*"; input.setAttribute("capture", "environment");
      input.onchange = async () => {
        const file = input.files?.[0]; if (!file) return;
        const orig = b.textContent; b.disabled = true; b.textContent = "Uploading…";
        try {
          const blob = await compressImage(file);
          const key = await uploadPhoto(client.gameId, client.seekerKey, blob);
          client.submitCurseClear(b.dataset.inst!, "photo", key);
        } catch { alert("Couldn't upload the photo. Try again."); b.disabled = false; b.textContent = orig; }
      };
      input.click();
    }));
    el.querySelector("#pod-found")?.addEventListener("click", () => { if (confirm("Report that your squad found the hiding pod? This banks their hide time.")) client.podFound(); });
    el.querySelector("#sk-caught")?.addEventListener("click", () => { if (confirm("Report that your team caught the hider? This banks their run and scores the catch. The GM sets who hides next.")) client.reportCaught(); });
    el.querySelector("#hd-video")?.addEventListener("click", () => openHdVideo(client));
    el.querySelector("#recap-dl")?.addEventListener("click", async (e) => {
      const b = e.currentTarget as HTMLButtonElement; const orig = b.textContent;
      b.disabled = true; b.textContent = "🎬 Generating…";
      try { if (!(await downloadRoundRecap(client))) alert("No eliminations to recap yet — the team leader's board drives the recap."); }
      catch { alert("Couldn't generate the recap."); }
      b.disabled = false; b.textContent = orig;
    });
    wireTeamMap(el);
  }

  // ---- top bar + live updates -------------------------------------------
  function renderTop() {
    const d = hiderRunDisplay();
    const chip = root.querySelector<HTMLElement>("#sk-runchip")!;
    chip.hidden = d.phase === "idle";
    root.querySelector<HTMLElement>("#sk-runchip-clk")!.textContent = d.text;
    chip.dataset.phase = d.phase;
    const curseChip = root.querySelector<HTMLElement>("#sk-cursechip");
    if (curseChip) curseChip.hidden = !client.amCursed;
  }

  client.subscribe(() => {
    gameMap.refresh(client);
    gameMap.syncPins(client.board.pins, (id, lng, lat) => client.movePin(id, lng, lat), (id) => client.removePin(id));
    gameMap.syncNotes(client.board.notes, (id, lng, lat) => client.moveNote(id, lng, lat),
      (id) => { const n = client.board.notes.find((x) => x.id === id); const v = prompt("Edit note", n?.text || ""); if (v != null) client.editNote(id, v); },
      (id) => client.removeNote(id));
    gameMap.syncTeammates([...client.locations.values()], client.identity.sub);
    const ro = root.querySelector<HTMLElement>("#sk-ro-banner"); if (ro) ro.hidden = client.canEditActive;
    const tbUndo = toolBadge.querySelector<HTMLButtonElement>(".tb-undo");
    if (tbUndo) tbUndo.disabled = !client.board.canUndo;
    renderTop();
    if (tab === "plan") renderPlan();
  });
  // Re-render the plan each second ONLY when a timer curse is counting down (so
  // the countdown ticks) — re-rendering otherwise would reload the card image.
  setInterval(() => { renderTop(); if (tab === "plan" && client.myCurses.some((c) => c.status !== "cleared" && curseById(c.curseId)?.clear.mode === "timer")) renderPlan(); }, 1000);

  renderLayers();
  LAYER_GROUPS.flatMap((g) => g.tiles).forEach((t) => applyTile(t, DEFAULT_ON.has(t.id))); // sync map to defaults
  renderLayers();
  // When a GM is reviewing a player (👁), open straight to the Team tab so the
  // team-leader / canonical-map state is visible immediately. Pod seekers also open
  // there ("Squad" tab) so their squad assignment + "found" button are front-and-center.
  setTab(client.identity.imp || client.mode === "podrotation" ? "plan" : "layers");
}

function clockTime(ts: number): string { const d = new Date(ts); return `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`; }
